import { useEffect, useState } from 'react'
import {
  AlertTriangle,
  ArrowDownToLine,
  Banknote,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Receipt,
  Wallet,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StatCard } from '@/components/StatCard'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/locale'
import {
  useAdminPayouts,
  useAdminProcessPayout,
  useAdminRevenuePayments,
  useAdminTenantPayouts,
  useAdminUpdateFee,
} from '@/features/invoicing/payments/queries'
import type {
  AdminRefundRow,
  AdminRevenuePaymentRow,
  ManagedPaymentRow,
  PayoutDestination,
  PayoutRow,
} from '@/features/invoicing/payments/types'

function formatMoneyCents(cents: number, currency: string): string {
  const amount = cents / 100
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

function bpsToPercent(bps: number): string {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`
}

function errMsg(err: unknown, fallback = 'Action failed.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

interface RevenueDrillDown {
  currency: string
  tenantId: string | null
  tenantName: string | null
}

export function ManagedPayoutsTab() {
  const listQ = useAdminPayouts()
  const [openTenantId, setOpenTenantId] = useState<string | null>(null)
  const [revenueDrill, setRevenueDrill] = useState<RevenueDrillDown | null>(null)

  if (listQ.isLoading) {
    return (
      <Card><CardContent className="py-10"><LoadingSpinner /></CardContent></Card>
    )
  }
  if (listQ.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load managed payouts</AlertTitle>
        <AlertDescription>{errMsg(listQ.error)}</AlertDescription>
      </Alert>
    )
  }

  const data = listQ.data!
  const totalBokapsysRevenue = data.revenue.reduce((s, r) => s + r.bokapsysRevenueCents, 0)
  const totalPendingNet = data.tenants.reduce((s, t) => s + t.netCents, 0)
  const primaryCurrency = data.tenants[0]?.currency ?? data.revenue[0]?.currency ?? 'USD'
  const refundsSummary = data.refundsSummary
  const refundsTotal = refundsSummary?.byCurrency.reduce((s, r) => s + r.grossRefundedCents, 0) ?? 0
  const refundsCount = refundsSummary?.byCurrency.reduce((s, r) => s + r.refundCount, 0) ?? 0
  const refundsCurrency = refundsSummary?.byCurrency[0]?.currency ?? primaryCurrency
  const recentRefunds = data.refunds ?? []
  const pendingRefunds = data.pendingRefunds ?? []

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard
          label="Tenants pending"
          value={String(data.tenants.length)}
          hint="awaiting payout"
          icon={Wallet}
        />
        <StatCard
          label="Total pending net"
          value={data.tenants.length === 0 ? '-' : formatMoneyCents(totalPendingNet, primaryCurrency)}
          hint="owed to tenants"
          icon={Banknote}
        />
        <StatCard
          label="Bokapsys revenue (all-time)"
          value={data.revenue.length === 0 ? '-' : formatMoneyCents(totalBokapsysRevenue, primaryCurrency)}
          hint="1% of every managed payment"
          icon={Receipt}
        />
        <StatCard
          label="Refunds (30d)"
          value={refundsCount === 0 ? '-' : formatMoneyCents(refundsTotal, refundsCurrency)}
          hint={refundsCount === 0 ? 'none' : `${refundsCount} refund${refundsCount === 1 ? '' : 's'}`}
          icon={AlertTriangle}
        />
        <StatCard
          label="Currencies"
          value={String(new Set(data.tenants.map((t) => t.currency)).size || 0)}
          icon={CreditCard}
        />
      </div>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <div>
            <h3 className="text-base font-semibold">Tenants with pending balance</h3>
            <p className="text-sm text-muted-foreground">
              Click a row to drill into the tenant&apos;s pending payments and process a payout.
            </p>
          </div>
          {data.tenants.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title="All caught up"
              description="No tenants currently have unpaid managed-mode balances."
            />
          ) : (
            <DataTable
              rows={data.tenants}
              columns={[
                { header: 'Tenant', accessorKey: 'tenantName',
                  cell: ({ row }) => (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto px-1 py-0.5 text-left text-sm font-semibold"
                      onClick={() => setOpenTenantId(row.original.tenantId)}
                    >
                      {row.original.tenantName}
                    </Button>
                  ),
                },
                { header: 'Currency', accessorKey: 'currency',
                  cell: ({ row }) => <Badge variant="default">{row.original.currency}</Badge> },
                { header: 'Fee', accessorKey: 'feeBps',
                  cell: ({ row }) => bpsToPercent(row.original.feeBps) },
                { header: 'Payments', accessorKey: 'paymentCount',
                  cell: ({ row }) => <span className="font-mono text-xs">{row.original.paymentCount}</span> },
                {
                  header: () => <span className="block text-right">Gross (net of refunds)</span>,
                  accessorKey: 'grossCents',
                  cell: ({ row }) => (
                    <span className="block text-right font-mono text-xs">
                      {formatMoneyCents(row.original.grossCents, row.original.currency)}
                    </span>
                  ),
                },
                {
                  header: () => <span className="block text-right">Refunded</span>,
                  accessorKey: 'refundedCents',
                  cell: ({ row }) => (
                    <span className="block text-right font-mono text-xs text-destructive">
                      {row.original.refundedCents > 0
                        ? `−${formatMoneyCents(row.original.refundedCents, row.original.currency)}`
                        : <span className="text-muted-foreground/60">-</span>}
                    </span>
                  ),
                },
                {
                  header: () => <span className="block text-right">Bokapsys fee</span>,
                  accessorKey: 'bokapsysFeeCents',
                  cell: ({ row }) => (
                    <span className="block text-right font-mono text-xs text-muted-foreground">
                      {formatMoneyCents(row.original.bokapsysFeeCents, row.original.currency)}
                    </span>
                  ),
                },
                {
                  header: () => <span className="block text-right">Net to tenant</span>,
                  accessorKey: 'netCents',
                  cell: ({ row }) => (
                    <span className="block text-right font-mono text-xs font-semibold">
                      {formatMoneyCents(row.original.netCents, row.original.currency)}
                    </span>
                  ),
                },
                { header: 'Oldest', accessorKey: 'oldestPaymentAt',
                  cell: ({ row }) => <span className="text-xs">{formatDate(row.original.oldestPaymentAt)}</span> },
                {
                  header: '',
                  id: 'actions',
                  cell: ({ row }) => (
                    <Button
                      size="sm"
                      onClick={() => setOpenTenantId(row.original.tenantId)}
                    >
                      <ArrowDownToLine className="size-4" /> Process
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </CardContent>
      </Card>

      {data.revenueByTenant && data.revenueByTenant.length > 0 ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <div>
              <h3 className="text-base font-semibold">Tenants with platform revenue</h3>
              <p className="text-sm text-muted-foreground">
                Per-tenant revenue across all settled managed-mode payments (paid out + pending).
                Click a tenant to drill in, or click the payment count to list the contributing payments.
              </p>
            </div>
            <DataTable
              rows={data.revenueByTenant}
              columns={[
                { header: 'Tenant', accessorKey: 'tenantName',
                  cell: ({ row }) => (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto px-1 py-0.5 text-left text-sm font-semibold"
                      onClick={() => setOpenTenantId(row.original.tenantId)}
                    >
                      {row.original.tenantName}
                    </Button>
                  ),
                },
                { header: 'Currency', accessorKey: 'currency',
                  cell: ({ row }) => <Badge variant="default">{row.original.currency}</Badge> },
                { header: 'Fee', accessorKey: 'feeBps',
                  cell: ({ row }) => bpsToPercent(row.original.feeBps) },
                { header: 'Payments', accessorKey: 'paymentCount',
                  cell: ({ row }) => (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto px-1 py-0.5 font-mono text-xs"
                      onClick={() => setRevenueDrill({
                        currency: row.original.currency,
                        tenantId: row.original.tenantId,
                        tenantName: row.original.tenantName,
                      })}
                    >
                      {row.original.paymentCount}
                      <span className="ml-1 text-[10px] text-muted-foreground">
                        ({row.original.paidCount} paid · {row.original.pendingCount} pending)
                      </span>
                    </Button>
                  ),
                },
                {
                  header: () => <span className="block text-right">Gross</span>,
                  accessorKey: 'grossCents',
                  cell: ({ row }) => (
                    <span className="block text-right font-mono text-xs">
                      {formatMoneyCents(row.original.grossCents, row.original.currency)}
                    </span>
                  ),
                },
                {
                  header: () => <span className="block text-right">Bokapsys revenue</span>,
                  accessorKey: 'bokapsysFeeCents',
                  cell: ({ row }) => (
                    <span className="block text-right font-mono text-xs font-semibold text-success">
                      {formatMoneyCents(row.original.bokapsysFeeCents, row.original.currency)}
                    </span>
                  ),
                },
              ]}
            />
          </CardContent>
        </Card>
      ) : null}

      {pendingRefunds.length > 0 ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <div>
              <h3 className="text-base font-semibold">Pending refunds</h3>
              <p className="text-sm text-muted-foreground">
                Refunds awaiting confirmation from Stripe. They drop off here once
                Stripe reports the refund as <strong>succeeded</strong> (or fails).
              </p>
            </div>
            <RefundsTable rows={pendingRefunds} onOpenTenant={setOpenTenantId} />
          </CardContent>
        </Card>
      ) : null}

      {recentRefunds.length > 0 ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <div>
              <h3 className="text-base font-semibold">Completed refunds</h3>
              <p className="text-sm text-muted-foreground">
                Refunds finalized by Stripe (succeeded, failed, or canceled).
                Clawback rows deduct from the tenant&apos;s next payout.
              </p>
            </div>
            <RefundsTable rows={recentRefunds} onOpenTenant={setOpenTenantId} />
          </CardContent>
        </Card>
      ) : null}

      {data.revenue.length > 0 ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <div>
              <h3 className="text-base font-semibold">Bokapsys platform revenue</h3>
              <p className="text-sm text-muted-foreground">
                Totals by currency across all tenants. Click a payment count to list the contributing payments.
              </p>
            </div>
            <DataTable
              rows={data.revenue}
              columns={[
                { header: 'Currency', accessorKey: 'currency',
                  cell: ({ row }) => <Badge variant="default">{row.original.currency}</Badge> },
                { header: 'Payments', accessorKey: 'paymentCount',
                  cell: ({ row }) => (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto px-1 py-0.5 font-mono text-xs"
                      onClick={() => setRevenueDrill({
                        currency: row.original.currency,
                        tenantId: null,
                        tenantName: null,
                      })}
                    >
                      {row.original.paymentCount}
                    </Button>
                  ),
                },
                {
                  header: () => <span className="block text-right">Gross processed</span>,
                  accessorKey: 'grossCents',
                  cell: ({ row }) => (
                    <span className="block text-right font-mono text-xs">
                      {formatMoneyCents(row.original.grossCents, row.original.currency)}
                    </span>
                  ),
                },
                {
                  header: () => <span className="block text-right">Bokapsys revenue</span>,
                  accessorKey: 'bokapsysRevenueCents',
                  cell: ({ row }) => (
                    <span className="block text-right font-mono text-xs font-semibold text-success">
                      {formatMoneyCents(row.original.bokapsysRevenueCents, row.original.currency)}
                    </span>
                  ),
                },
              ]}
            />
          </CardContent>
        </Card>
      ) : null}

      <TenantDrillDownModal
        tenantId={openTenantId}
        onClose={() => setOpenTenantId(null)}
      />

      <RevenueDrillDownModal
        drill={revenueDrill}
        onClose={() => setRevenueDrill(null)}
      />
    </div>
  )
}

// ── Drill-down: tenant detail + process payout flow ──────────────────

interface ProcessForm {
  currency: string
  method: 'stripe_transfer' | 'manual_wire' | 'ach' | 'check'
  reference: string
  payoutAccountId: string
  periodStart: string
  periodEnd: string
}

function TenantDrillDownModal({
  tenantId,
  onClose,
}: {
  tenantId: string | null
  onClose: () => void
}) {
  const { toast } = useToast()
  const detailQ = useAdminTenantPayouts(tenantId)
  const processM = useAdminProcessPayout(tenantId)
  const updateFeeM = useAdminUpdateFee(tenantId)

  const [process, setProcess] = useState<ProcessForm>({
    currency: 'USD',
    method: 'manual_wire',
    reference: '',
    payoutAccountId: '',
    periodStart: '',
    periodEnd: '',
  })
  const [feeOpen, setFeeOpen] = useState(false)
  const [feePercent, setFeePercent] = useState('')

  const detail = detailQ.data
  const pending = detail?.pendingBalance ?? []

  // Default the form's currency to whatever appears first in the
  // pending list once it arrives.
  useEffect(() => {
    if (pending[0]?.currency && pending[0].currency !== process.currency) {
      setProcess((p) => ({ ...p, currency: pending[0].currency }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending])

  const onProcess = async () => {
    if (!process.currency) {
      toast({ variant: 'destructive', title: 'Pick a currency.' })
      return
    }
    try {
      const r = await processM.mutateAsync({
        currency: process.currency,
        method: process.method,
        reference: process.reference.trim() || null,
        payoutAccountId: process.payoutAccountId ? Number(process.payoutAccountId) : null,
        periodStart: process.periodStart || null,
        periodEnd: process.periodEnd || null,
      })
      toast({
        title: 'Payout recorded.',
        description: `Paid ${r.paymentCount} payment${r.paymentCount === 1 ? '' : 's'} totalling ${formatMoneyCents(r.netCents, r.currency)}.`,
      })
      // Reset reference; keep the rest in case staff is processing multiple currencies.
      setProcess((p) => ({ ...p, reference: '' }))
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not process', description: errMsg(err) })
    }
  }

  const onUpdateFee = async () => {
    const pct = Number(feePercent)
    if (!Number.isFinite(pct) || pct < 0 || pct > 50) {
      toast({ variant: 'destructive', title: 'Fee must be 0–50 %' })
      return
    }
    try {
      const bps = Math.round(pct * 100)
      await updateFeeM.mutateAsync(bps)
      toast({ title: 'Fee updated', description: `Bokapsys fee now ${bpsToPercent(bps)} for this tenant.` })
      setFeeOpen(false)
    } catch (err) {
      toast({ variant: 'destructive', title: 'Could not update fee', description: errMsg(err) })
    }
  }

  return (
    <Modal
      open={tenantId != null}
      onOpenChange={(v) => !v && onClose()}
      title={detail?.tenant?.name ? `Payouts for ${detail.tenant.name}` : 'Tenant payouts'}
      className="max-w-5xl"
    >
      {detailQ.isLoading ? <LoadingSpinner /> : null}
      {detail ? (
        <div className="space-y-4">
          {/* Header summary */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
            <div className="flex items-center gap-3">
              <CreditCard className="size-5 text-primary" />
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Bokapsys fee for this tenant
                </div>
                <div className="font-semibold">
                  {bpsToPercent(detail.tenant?.feeBps ?? 100)}
                </div>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setFeePercent(((detail.tenant?.feeBps ?? 100) / 100).toString())
                setFeeOpen(true)
              }}
            >
              Override fee
            </Button>
          </div>

          {detail.payoutDestination ? (
            <BankDetailsCard destination={detail.payoutDestination} />
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertTitle>No payout account on file</AlertTitle>
              <AlertDescription>
                The tenant hasn&apos;t configured a payout account yet. You can still record a
                payout if you handled it out-of-band, but record the reference for audit.
              </AlertDescription>
            </Alert>
          )}

          {/* Pending balance + process form */}
          <Card>
            <CardContent className="space-y-3 pt-6">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Pending - process payout
              </h4>
              {pending.length === 0 ? (
                <EmptyState
                  icon={CheckCircle2}
                  title="No pending balance"
                  description="No unpaid managed-mode payments for this tenant."
                />
              ) : (
                <>
                  <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
                    {pending.map((b) => (
                      <div
                        key={b.currency}
                        className={[
                          'cursor-pointer rounded-md border p-3 transition-colors',
                          process.currency === b.currency
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-muted-foreground/40',
                        ].join(' ')}
                        onClick={() => setProcess((p) => ({ ...p, currency: b.currency }))}
                      >
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                          {b.currency} ({b.paymentCount} pmt)
                        </div>
                        <div className="mt-1 font-mono text-base font-semibold">
                          {formatMoneyCents(b.netCents, b.currency)}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          gross {formatMoneyCents(b.grossCents, b.currency)} · stripe −{formatMoneyCents(b.stripeFeeCents, b.currency)} · bokapsys −{formatMoneyCents(b.bokapsysFeeCents, b.currency)}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FormField
                      id="payout-method"
                      label="Method"
                      control={
                        <Select
                          value={process.method}
                          onValueChange={(v) => setProcess((p) => ({ ...p, method: v as ProcessForm['method'] }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manual_wire">Manual wire</SelectItem>
                            <SelectItem value="ach">ACH</SelectItem>
                            <SelectItem value="check">Check</SelectItem>
                            <SelectItem value="stripe_transfer">Stripe transfer</SelectItem>
                          </SelectContent>
                        </Select>
                      }
                    />
                    <FormField
                      id="payout-ref"
                      label="Reference / wire ID"
                      hint="e.g. Wire confirmation number, transfer ID, or check number."
                      control={
                        <Input
                          id="payout-ref"
                          value={process.reference}
                          onChange={(e) => setProcess((p) => ({ ...p, reference: e.currentTarget.value }))}
                        />
                      }
                    />
                    <FormField
                      id="period-start"
                      label="Period start (optional)"
                      control={
                        <Input
                          id="period-start"
                          type="date"
                          value={process.periodStart}
                          onChange={(e) => setProcess((p) => ({ ...p, periodStart: e.currentTarget.value }))}
                        />
                      }
                    />
                    <FormField
                      id="period-end"
                      label="Period end (optional)"
                      control={
                        <Input
                          id="period-end"
                          type="date"
                          value={process.periodEnd}
                          onChange={(e) => setProcess((p) => ({ ...p, periodEnd: e.currentTarget.value }))}
                        />
                      }
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button onClick={onProcess} disabled={processM.isPending || pending.length === 0}>
                      <ArrowDownToLine className="size-4" />
                      {processM.isPending ? 'Processing…' : `Mark ${process.currency} payout paid`}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Recent payments */}
          {detail.recentPayments.length > 0 ? (
            <Card>
              <CardContent className="space-y-2 pt-6">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Recent payments (latest 100)
                </h4>
                <RecentPaymentsTable rows={detail.recentPayments} />
              </CardContent>
            </Card>
          ) : null}

          {/* Past payouts */}
          {detail.payouts.length > 0 ? (
            <Card>
              <CardContent className="space-y-2 pt-6">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Payout history
                </h4>
                <PayoutHistoryTable rows={detail.payouts} />
              </CardContent>
            </Card>
          ) : null}
        </div>
      ) : null}

      {/* Fee override sub-modal */}
      <Modal open={feeOpen} onOpenChange={setFeeOpen} title="Override Bokapsys fee for this tenant">
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Default platform fee is 1% (100 basis points). Overriding here applies only to this
            tenant&apos;s future payments. Past payments keep the fee they were tagged with.
          </p>
          <FormField
            id="fee-percent"
            label="Fee % (e.g. 1, 0.5, 2.5)"
            control={
              <Input
                id="fee-percent"
                type="number"
                min={0}
                max={50}
                step={0.01}
                value={feePercent}
                onChange={(e) => setFeePercent(e.currentTarget.value)}
              />
            }
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setFeeOpen(false)}>Cancel</Button>
            <Button onClick={onUpdateFee} disabled={updateFeeM.isPending}>
              {updateFeeM.isPending ? 'Saving…' : 'Save fee'}
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  )
}

function RecentPaymentsTable({ rows }: { rows: ManagedPaymentRow[] }) {
  return (
    <DataTable
      rows={rows}
      columns={[
        { header: 'Date', accessorKey: 'receivedAt',
          cell: ({ row }) => <span className="text-xs">{formatDate(row.original.receivedAt)}</span> },
        { header: 'Invoice', accessorKey: 'invoiceNumber',
          cell: ({ row }) => row.original.invoiceNumber ?? '-' },
        { header: 'Customer', accessorKey: 'customerName',
          cell: ({ row }) => row.original.customerName ?? '-' },
        {
          header: () => <span className="block text-right">Gross</span>,
          accessorKey: 'grossCents',
          cell: ({ row }) => (
            <span className="block text-right font-mono text-xs">
              {formatMoneyCents(row.original.grossCents, row.original.currency)}
            </span>
          ),
        },
        {
          header: () => <span className="block text-right">Net</span>,
          accessorKey: 'netCents',
          cell: ({ row }) => (
            <span className="block text-right font-mono text-xs font-semibold">
              {formatMoneyCents(row.original.netCents, row.original.currency)}
            </span>
          ),
        },
        {
          header: 'Status',
          accessorKey: 'paid',
          cell: ({ row }) => row.original.paid
            ? <Badge variant="success">Paid</Badge>
            : <Badge variant="secondary">Pending</Badge>,
        },
      ]}
    />
  )
}

function BankDetailsCard({ destination }: { destination: PayoutDestination }) {
  const [open, setOpen] = useState(false)
  const fields: Array<{ label: string; value: string | null | undefined }> = [
    { label: 'Account label', value: destination.label },
    { label: 'Bank name', value: destination.bankName },
    { label: 'Account holder', value: destination.accountName },
    {
      label: 'Account number',
      value: destination.accountNumberLast4 ? `••••${destination.accountNumberLast4}` : null,
    },
    { label: 'Routing number (US)', value: destination.routingNumber },
    { label: 'Sort code (UK)', value: destination.sortCode },
    { label: 'IBAN', value: destination.iban },
    { label: 'SWIFT / BIC', value: destination.swift },
  ]
  const present = fields.filter((f) => f.value)
  return (
    <Card>
      <CardContent className="pt-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-3 text-left"
          aria-expanded={open}
        >
          <div className="flex items-center gap-3">
            <Banknote className="size-5 text-primary" />
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Pay to
              </div>
              <div className="font-semibold">
                {destination.bankName ?? destination.label}
                {destination.accountNumberLast4 ? ` ••••${destination.accountNumberLast4}` : ''}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {open ? 'Hide details' : 'Show full bank details'}
            {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </div>
        </button>
        {open ? (
          <div className="mt-4 grid gap-3 border-t pt-4 sm:grid-cols-2">
            {present.length === 0 ? (
              <div className="text-sm text-muted-foreground">No bank details on file.</div>
            ) : (
              present.map((f) => (
                <div key={f.label} className="space-y-0.5">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {f.label}
                  </div>
                  <div className="font-mono text-sm">{f.value}</div>
                </div>
              ))
            )}
            {destination.isPayoutDestination ? (
              <div className="space-y-0.5">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Marked as payout destination
                </div>
                <div className="text-sm">
                  <Badge variant="success">Yes</Badge>
                </div>
              </div>
            ) : null}
            {!destination.isActive ? (
              <div className="space-y-0.5">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Status
                </div>
                <div className="text-sm">
                  <Badge variant="danger">Inactive</Badge>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function RevenueDrillDownModal({
  drill,
  onClose,
}: {
  drill: RevenueDrillDown | null
  onClose: () => void
}) {
  const q = useAdminRevenuePayments(
    drill?.currency ?? null,
    drill?.tenantId ?? null,
    drill != null,
  )
  const rows = q.data?.payments ?? []
  const title = drill
    ? drill.tenantName
      ? `${drill.currency} payments - ${drill.tenantName}`
      : `${drill.currency} payments - all tenants`
    : 'Payments'
  const totalGross = rows.reduce((s, r) => s + r.grossCents, 0)
  const totalBokapsys = rows.reduce((s, r) => s + r.bokapsysFeeCents, 0)
  return (
    <Modal
      open={drill != null}
      onOpenChange={(v) => !v && onClose()}
      title={title}
      className="max-w-5xl"
    >
      {q.isLoading ? <LoadingSpinner /> : null}
      {q.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load payments</AlertTitle>
          <AlertDescription>{errMsg(q.error)}</AlertDescription>
        </Alert>
      ) : null}
      {drill && q.data ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Payments" value={String(rows.length)} />
            <StatCard
              label="Paid out"
              value={String(rows.filter((r) => r.paid).length)}
            />
            <StatCard
              label="Gross"
              value={formatMoneyCents(totalGross, drill.currency)}
            />
            <StatCard
              label="Bokapsys revenue"
              value={formatMoneyCents(totalBokapsys, drill.currency)}
            />
          </div>
          {rows.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title="No payments"
              description="No managed-mode payments match this filter."
            />
          ) : (
            <RevenuePaymentsTable rows={rows} showTenant={drill.tenantId == null} />
          )}
        </div>
      ) : null}
    </Modal>
  )
}

function RevenuePaymentsTable({
  rows,
  showTenant,
}: {
  rows: AdminRevenuePaymentRow[]
  showTenant: boolean
}) {
  return (
    <DataTable
      rows={rows}
      columns={[
        { header: 'Date', accessorKey: 'receivedAt',
          cell: ({ row }) => <span className="text-xs">{formatDate(row.original.receivedAt)}</span> },
        ...(showTenant ? [{
          header: 'Tenant',
          accessorKey: 'tenantName' as const,
          cell: ({ row }: { row: { original: AdminRevenuePaymentRow } }) => (
            <span className="text-sm font-medium">{row.original.tenantName}</span>
          ),
        }] : []),
        { header: 'Invoice', accessorKey: 'invoiceNumber',
          cell: ({ row }) => row.original.invoiceNumber ?? '-' },
        { header: 'Customer', accessorKey: 'customerName',
          cell: ({ row }) => row.original.customerName ?? '-' },
        {
          header: () => <span className="block text-right">Gross</span>,
          accessorKey: 'grossCents',
          cell: ({ row }) => (
            <span className="block text-right font-mono text-xs">
              {formatMoneyCents(row.original.grossCents, row.original.currency)}
            </span>
          ),
        },
        {
          header: () => <span className="block text-right">Bokapsys fee</span>,
          accessorKey: 'bokapsysFeeCents',
          cell: ({ row }) => (
            <span className="block text-right font-mono text-xs text-success">
              {formatMoneyCents(row.original.bokapsysFeeCents, row.original.currency)}
            </span>
          ),
        },
        {
          header: 'Status',
          accessorKey: 'paid',
          cell: ({ row }) => row.original.paid
            ? <Badge variant="success">Paid out</Badge>
            : <Badge variant="secondary">Pending</Badge>,
        },
        {
          header: 'Reference',
          accessorKey: 'payoutReference',
          cell: ({ row }) => row.original.payoutReference
            ? <span className="font-mono text-xs">{row.original.payoutReference}</span>
            : '-',
        },
      ]}
    />
  )
}

function PayoutHistoryTable({ rows }: { rows: PayoutRow[] }) {
  return (
    <DataTable
      rows={rows}
      columns={[
        { header: 'Paid', accessorKey: 'paidAt',
          cell: ({ row }) => <span className="text-xs">{row.original.paidAt ? formatDate(row.original.paidAt) : '-'}</span> },
        { header: 'Method', accessorKey: 'method',
          cell: ({ row }) => row.original.method ? <Badge variant="outline">{row.original.method.replace(/_/g, ' ')}</Badge> : '-' },
        { header: 'Reference', accessorKey: 'reference',
          cell: ({ row }) => row.original.reference ? <span className="text-xs font-mono">{row.original.reference}</span> : '-' },
        {
          header: () => <span className="block text-right">Net</span>,
          accessorKey: 'netCents',
          cell: ({ row }) => (
            <span className="block text-right font-mono text-xs font-semibold">
              {formatMoneyCents(row.original.netCents, row.original.currency)}
            </span>
          ),
        },
        { header: 'Status', accessorKey: 'status',
          cell: ({ row }) => {
            const v = row.original.status === 'paid' ? 'success'
              : row.original.status === 'failed' ? 'danger' : 'secondary'
            return <Badge variant={v}>{row.original.status}</Badge>
          },
        },
      ]}
    />
  )
}

function RefundsTable({
  rows,
  onOpenTenant,
}: {
  rows: AdminRefundRow[]
  onOpenTenant: (tenantId: string) => void
}) {
  return (
    <DataTable
      rows={rows}
      columns={[
        {
          header: 'Tenant',
          accessorKey: 'tenantName',
          cell: ({ row }) => (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-1 py-0.5 text-left text-sm font-semibold"
              onClick={() => onOpenTenant(row.original.tenantId)}
            >
              {row.original.tenantName}
            </Button>
          ),
        },
        {
          header: 'Invoice',
          accessorKey: 'invoiceNumber',
          cell: ({ row }) => (
            <span className="font-mono text-xs">{row.original.invoiceNumber ?? '-'}</span>
          ),
        },
        {
          header: 'Customer',
          accessorKey: 'customerName',
          cell: ({ row }) => (
            <span className="text-xs">{row.original.customerName ?? '-'}</span>
          ),
        },
        {
          header: 'Status',
          accessorKey: 'status',
          cell: ({ row }) => {
            const s = row.original.status
            const v: 'success' | 'danger' | 'secondary' | 'default' =
              s === 'succeeded' ? 'success'
              : s === 'failed' || s === 'canceled' ? 'danger'
              : s === 'void' ? 'secondary' : 'default'
            return <Badge variant={v}>{s}</Badge>
          },
        },
        {
          header: () => <span className="block text-right">Amount</span>,
          accessorKey: 'grossCents',
          cell: ({ row }) => (
            <span className="block text-right font-mono text-xs font-semibold">
              {formatMoneyCents(row.original.grossCents, row.original.currency)}
            </span>
          ),
        },
        {
          header: () => <span className="block text-right">Clawback</span>,
          accessorKey: 'clawback',
          cell: ({ row }) =>
            row.original.clawback ? (
              <Badge variant="warning">payout deduct</Badge>
            ) : (
              <span className="block text-right text-xs text-muted-foreground">-</span>
            ),
        },
        {
          header: 'Issued',
          accessorKey: 'issuedAt',
          cell: ({ row }) => (
            <span className="text-xs">{formatDate(row.original.issuedAt)}</span>
          ),
        },
      ]}
    />
  )
}
